import React, {useEffect} from 'react'
import Input from "../../presentational/Input";
import Button from "../../presentational/Button";
import { Link, useNavigate } from 'react-router-dom';
import LocalizedText from '../../presentational/LocalizedText';
import useTranslate from "../../../hooks/useTranslate";
import { isValidEmailOrPhone } from '../utils'
import './Login.css'
import { ClientError, LoginLink, UserType } from '../types';
import { useConfiguration } from './Configuration';
import { useTurnstile } from "../../../hooks/useTurnstile";

interface LoginProps {
  emailOrPhone: string
  serverError: string
  joinLink: string
  clientErrors: ClientError[]
  isLoading: boolean
  externalLoginLinks: LoginLink[]
  startLogin: () => void
  setEmailOrPhone: (emailOrPhone: string) => void
}

const Login: React.FC<LoginProps> = ({
  emailOrPhone,
  serverError,
  clientErrors,
  joinLink,
  externalLoginLinks,
  isLoading,
  startLogin,
  setEmailOrPhone
}) => {
  const navigate = useNavigate()
  const translations = useTranslate(["participant-login-next-label", "participant-login-email-or-phone-label"])
  const { configuration } = useConfiguration()
  const { userType } = configuration;
  useTurnstile();

  const handleChangeEmailOrPhone = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmailOrPhone(e.target.value)
  }

  const handleClickJoin = (e: React.MouseEvent<HTMLButtonElement>) => {
    navigate(joinLink)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    startLogin()
    return false
  }

  return (
    <form className="login-action login" onSubmit={handleSubmit}>
      <h3><LocalizedText textKey="participant-login-login-title" /></h3>
      {
        serverError && (
          <p className="server-error">{serverError}</p>
        )
      }
      {
        clientErrors?.some((ce) => ce === 'noAccountLogin') && (
          <div className="client-error-container">
            <p className="client-error"><LocalizedText textKey="participant-login-login-non-existent-account" /></p>
            <Button className="non-existent-account-button" intent="secondary" onClick={handleClickJoin}><LocalizedText textKey="participant-login-login-click-here-to-join" /></Button>
          </div>
        )
      }
      <div className="field-group">
        <label className="login-field" htmlFor="username">{translations["participant-login-email-or-phone-label"]}</label>
        <Input
          onChange={handleChangeEmailOrPhone}
          className="autofocus"
          disabled={isLoading}
          id="username"
          value={emailOrPhone}
          type="text"
          placeholder="participant@example.com or 555-555-5555"
          autoComplete="username"
          autoFocus={true}
          fullWidth={true}
          htmlAttributes={{
            onInput: handleChangeEmailOrPhone,
            spellCheck: false,
            autoCapitalize: "none",
            autoCorrect: "off",
          }}
        />
      </div>
      <div>
        <div id="cf-turnstile" className="cf-turnstile"></div>
        <Button
          onClick={handleSubmit}
          disabled={(userType === UserType.participant ? !isValidEmailOrPhone(emailOrPhone) : false) || isLoading}
          className="primary-action"
          width="100%"
          intent="primary"
          loading={isLoading}
          type="submit"
        >
        <span dangerouslySetInnerHTML={{ __html: translations["participant-login-next-label"] }} />
      </Button>
      </div>
      {
        joinLink &&
        <p className="sub-action">
          <span><LocalizedText textKey="participant-login-login-new-user" /></span> <Link to={joinLink}><LocalizedText textKey="participant-login-login-click-here-to-join-label" /></Link>
        </p>
      }
      {
        externalLoginLinks?.map((link) => (
          <p
            key={link.Name}
            className="sub-action"
          >
            <a
              href={link.Href}
              className="brand-color login-controls-link"
            >
              <LocalizedText
                textKey="participant-login-signin-external"
                values={{ externalIdentityProviderName: link.Name }}
              />
            </a>
          </p>
        ))
      }
    </form>
  )
}

export default Login